/**
 * Dados da Home: totais por tipo, listas, cidades e regiões das listas de negócios locais e o
 * estado da licença. Os totais vêm das listas (com as contagens locais do local-first); antes
 * de elas chegarem, do /app/me.
 */
import { useEffect, useMemo, useState } from 'react';
import { useMe } from '../../api/useMe';
import { useLists } from '../../api/useLists';
import { useSessionStore } from '../../stores/sessionStore';
import type { SearchList } from '../../types/searchList';
import {
  HOME_MODULES,
  coverageOf,
  groupRegions,
  licenseFrom,
  summarizeCities,
  type CitySummary,
  type Coverage,
  type HomeList,
  type HomeModule,
  type LicenseInfo,
  type RegionSummary,
} from './homeData';
import { loadMunicipalityFinder, type FindMunicipality } from './municipalities';

export interface HomeDashboard {
  firstName: string;
  license: LicenseInfo | null;
  totals: Record<HomeModule, number>;
  totalContacts: number;
  /** Listas com leads; null enquanto as listas não chegaram. */
  totalLists: number | null;
  cities: CitySummary[];
  regions: RegionSummary[];
  coverage: Coverage;
  /** Sem nenhum lead capturado (só depois de as listas chegarem). */
  empty: boolean;
}

function toHomeLists(module: HomeModule, lists: SearchList[] | undefined): HomeList[] {
  return (lists ?? [])
    .filter((l) => Number(l.count) > 0)
    .map((l) => ({ id: l._id, module, count: Number(l.count), locations: l.locations ?? [] }));
}

export function useHomeDashboard(): HomeDashboard {
  const me = useMe();
  const session = useSessionStore((s) => s.session);
  const places = useLists('places');
  const phones = useLists('phones');
  const emails = useLists('emails');
  const [finder, setFinder] = useState<FindMunicipality | null>(null);

  const listsReady =
    (places.isSuccess || places.isError) &&
    (phones.isSuccess || phones.isError) &&
    (emails.isSuccess || emails.isError);
  const needsFinder = (places.data ?? []).some((l) => Number(l.count) > 0 && (l.locations?.length ?? 0) > 0);

  useEffect(() => {
    if (!needsFinder || finder) return;
    let alive = true;
    loadMunicipalityFinder()
      .then((find) => {
        if (alive) setFinder(() => find);
      })
      .catch(() => {
        /* Sem a tabela, as cidades ficam sem estado (contadas como fora do Brasil). */
      });
    return () => {
      alive = false;
    };
  }, [needsFinder, finder]);

  return useMemo(() => {
    const lists = [
      ...toHomeLists('places', places.data),
      ...toHomeLists('phones', phones.data),
      ...toHomeLists('emails', emails.data),
    ];
    const meTotals = me.data?.totals;
    const totals = Object.fromEntries(
      HOME_MODULES.map((module) => [
        module,
        listsReady
          ? lists.filter((l) => l.module === module).reduce((sum, l) => sum + l.count, 0)
          : Number(meTotals?.[module] ?? 0),
      ]),
    ) as Record<HomeModule, number>;
    // Enquanto a tabela de municípios não chega, as cidades esperam (para não piscar "fora do Brasil").
    const cities = needsFinder && !finder ? [] : summarizeCities(lists, finder);
    const totalContacts = totals.places + totals.phones + totals.emails;
    // Antes de as listas chegarem, o /me já diz se a conta tem contatos. Sem nenhum, o Início abre
    // direto na versão sem listas (B2), sem piscar a 3B zerada; se as listas mostrarem o
    // contrário, ele troca para a 3B.
    const meSemContatos =
      Boolean(meTotals) &&
      Number(meTotals?.general ?? 0) === 0 &&
      HOME_MODULES.every((module) => Number(meTotals?.[module] ?? 0) === 0);
    return {
      firstName: (me.data?.name ?? '').trim().split(/\s+/)[0] ?? '',
      license: licenseFrom(session?.expiredAt, new Date()),
      totals,
      totalContacts,
      totalLists: listsReady ? lists.length : null,
      cities,
      regions: groupRegions(cities),
      coverage: coverageOf(cities),
      empty: listsReady ? lists.length === 0 && totalContacts === 0 : meSemContatos,
    };
  }, [me.data, session?.expiredAt, places.data, phones.data, emails.data, listsReady, needsFinder, finder]);
}
